import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import React from "react";
import { TreeCardProps } from "../../lib/types/types";
import AiSummaryButton from "./aiSummaryButton";

// Format date for display
const formatDate = (dateString: string): string => {
  try {
    // Treat input as UTC. If there's no timezone info, append 'Z' to force UTC parsing.
    let ds = dateString;
    if (typeof ds === "string") {
      const hasTimeZone =
        /(?:Z|[+\-]\d{2}:?\d{2}|[+\-]\d{4}|GMT|UTC|\([^)]+\))/i.test(ds);
      if (!hasTimeZone) {
        ds = ds + "Z";
      }
    }

    const date = new Date(ds);
    if (isNaN(date.getTime())) return "Unknown date";

    const locale =
      typeof navigator !== "undefined" && navigator.language
        ? navigator.language
        : "en-US";
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone, // format in the user's local time zone
    }).format(date);
  } catch (e) {
    return "Unknown date";
  }
};

export const TreeCard: React.FC<TreeCardProps> = ({
  id,
  filename,
  updatedAt,
  createdAt,
  canDelete,
  onEdit,
  onDelete,
  disabled = false,
}) => {
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "transform 0.2s, box-shadow 0.2s",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled
          ? {}
          : {
              transform: "translateY(-5px)",
              boxShadow: 4,
            },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography variant="h6" component="h2" gutterBottom>
            {filename}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Updated: {formatDate(updatedAt)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Created: {formatDate(createdAt)}
          </Typography>
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", p: 1 }}>
        <AiSummaryButton treeId={id} />
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => !disabled && onEdit(id)}
          variant="contained"
          disabled={disabled}
          title={disabled ? "Subscribe to edit" : "Edit"}
        >
          Edit
        </Button>
        {canDelete && (
          <IconButton
            size="small"
            color="error"
            onClick={() => !disabled && onDelete(id)}
            disabled={disabled}
            title={disabled ? "Subscribe to delete" : "Delete"}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
};
